import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import { 
  Resource, 
  ResourceCategory, 
  ResourceVisibility, 
  ResourceFilters,
  CreateResourceForm,
  VisibilityAssignment,
  ResourceType,
  DifficultyLevel
} from '../types/resources';

interface ResourceState {
  resources: Resource[];
  categories: ResourceCategory[];
  userResources: Resource[];
  currentResource: Resource | null;
  visibilities: ResourceVisibility[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchResources: (filters?: ResourceFilters) => Promise<void>;
  fetchUserResources: (userId?: string) => Promise<void>;
  fetchResource: (id: string) => Promise<void>;
  fetchCategories: () => Promise<void>;
  createResource: (resource: CreateResourceForm) => Promise<Resource>;
  updateResource: (id: string, updates: Partial<Resource>) => Promise<void>;
  deleteResource: (id: string) => Promise<void>;
  
  // Visibility Management
  assignResourceVisibility: (assignment: VisibilityAssignment) => Promise<void>;
  removeResourceVisibility: (resourceId: string, userId: string) => Promise<void>;
  fetchResourceVisibilities: (resourceId: string) => Promise<void>;
  
  // Interactions
  recordInteraction: (resourceId: string, type: string) => Promise<void>;
  toggleLike: (resourceId: string) => Promise<void>;
  
  // Utilities
  clearError: () => void;
}

// Mock categories for development
const MOCK_CATEGORIES: ResourceCategory[] = [
  {
    id: '1',
    name: 'Modèle Économique',
    slug: 'business-model',
    description: 'Construire et valider son modèle économique ESS',
    icon: '💼',
    color: 'blue',
    order_index: 1,
    is_active: true
  },
  {
    id: '2',
    name: 'Statut Juridique',
    slug: 'legal-structure',
    description: 'Choisir la forme juridique adaptée (association, coopérative, etc.)',
    icon: '⚖️',
    color: 'purple',
    order_index: 2,
    is_active: true
  },
  {
    id: '3',
    name: 'Financement',
    slug: 'financing',
    description: 'Trouver et obtenir des financements pour son projet ESS',
    icon: '💰',
    color: 'green',
    order_index: 3,
    is_active: true
  },
  {
    id: '4',
    name: 'Marketing & Communication',
    slug: 'marketing',
    description: 'Promouvoir son projet et toucher ses bénéficiaires',
    icon: '📢',
    color: 'orange',
    order_index: 4,
    is_active: true
  },
  {
    id: '5',
    name: 'Gestion & Management',
    slug: 'management',
    description: 'Organiser et gérer son projet au quotidien',
    icon: '📊',
    color: 'indigo',
    order_index: 5,
    is_active: true
  },
  {
    id: '6',
    name: 'Mesure d\'Impact',
    slug: 'impact-measurement',
    description: 'Évaluer et communiquer l\'impact social de son projet',
    icon: '📈',
    color: 'teal',
    order_index: 6,
    is_active: true
  },
  {
    id: '7',
    name: 'Réseautage',
    slug: 'networking',
    description: 'Développer son réseau et créer des partenariats',
    icon: '🤝',
    color: 'pink',
    order_index: 7,
    is_active: true
  },
  {
    id: '8',
    name: 'Outils Numériques',
    slug: 'digital-tools',
    description: 'Utiliser le numérique pour développer son projet',
    icon: '💻',
    color: 'cyan',
    order_index: 8,
    is_active: true
  },
  {
    id: '9',
    name: 'Développement Durable',
    slug: 'sustainability',
    description: 'Intégrer les enjeux environnementaux dans son projet',
    icon: '🌱',
    color: 'emerald',
    order_index: 9,
    is_active: true
  },
  {
    id: '10',
    name: 'Innovation Sociale',
    slug: 'innovation',
    description: 'Innover pour répondre aux besoins sociaux',
    icon: '💡',
    color: 'yellow',
    order_index: 10,
    is_active: true
  }
];

// Mock resources for development
const MOCK_RESOURCES: Resource[] = [
  {
    id: '1',
    title: 'Guide complet du Business Model Canvas pour l\'ESS',
    description: 'Apprenez à construire votre modèle économique avec l\'outil Business Model Canvas adapté aux spécificités de l\'Économie Sociale et Solidaire.',
    content: `# Business Model Canvas pour l'ESS

Le Business Model Canvas est un outil visuel qui permet de décrire, analyser et concevoir des modèles économiques. Pour l'ESS, nous l'adaptons pour intégrer la dimension sociale.

## Les 9 blocs du Canvas ESS

### 1. Segments de bénéficiaires
Qui sont vos bénéficiaires directs et indirects ?

### 2. Propositions de valeur sociale
Quelle valeur sociale créez-vous ?

### 3. Canaux de distribution
Comment atteignez-vous vos bénéficiaires ?

### 4. Relations avec les bénéficiaires
Quel type de relation entretenez-vous ?

### 5. Sources de revenus
Comment votre projet génère-t-il des revenus ?

### 6. Ressources clés
Quelles sont vos ressources essentielles ?

### 7. Activités clés
Quelles sont vos activités principales ?

### 8. Partenaires clés
Qui sont vos partenaires stratégiques ?

### 9. Structure de coûts
Quels sont vos principaux coûts ?

## Spécificités ESS

- Intégrer la mesure d'impact social
- Considérer les parties prenantes multiples
- Équilibrer viabilité économique et mission sociale`,
    type: ResourceType.GUIDE,
    category: 'BUSINESS_MODEL',
    tags: ['business model', 'canvas', 'modèle économique', 'stratégie'],
    author_id: 'coord-1',
    author_name: 'Sophie Martin',
    is_published: true,
    is_featured: true,
    difficulty_level: DifficultyLevel.INTERMEDIATE,
    estimated_reading_time: 15,
    view_count: 156,
    like_count: 23,
    created_at: '2024-01-10T09:00:00Z',
    updated_at: '2024-01-15T14:30:00Z',
    published_at: '2024-01-10T09:00:00Z'
  },
  {
    id: '2',
    title: 'Choisir entre Association et Coopérative en Guadeloupe',
    description: 'Comparatif détaillé des statuts juridiques disponibles pour les projets ESS en Guadeloupe, avec avantages et inconvénients.',
    content: `# Statuts Juridiques ESS en Guadeloupe

## Association Loi 1901

### Avantages
- Simplicité de création
- Fiscalité avantageuse
- Subventions publiques accessibles

### Inconvénients
- Activité commerciale limitée
- Gouvernance parfois complexe

## Coopérative (SCOP, SCIC)

### SCOP (Société Coopérative de Production)
- Salariés associés
- Gouvernance démocratique
- Partage des bénéfices

### SCIC (Société Coopérative d'Intérêt Collectif)
- Multi-sociétariat
- Utilité sociale reconnue
- Partenariats publics-privés

## Entreprise Solidaire d'Utilité Sociale (ESUS)

### Critères
- Utilité sociale dans l'objet
- Gouvernance démocratique
- Lucrativité limitée

### Avantages
- Accès aux financements solidaires
- Reconnaissance officielle
- Avantages fiscaux`,
    type: ResourceType.ARTICLE,
    category: 'LEGAL_STRUCTURE',
    tags: ['statut juridique', 'association', 'coopérative', 'SCOP', 'SCIC', 'ESUS'],
    author_id: 'coord-1',
    author_name: 'Jean-Claude Martin',
    is_published: true,
    is_featured: false,
    difficulty_level: DifficultyLevel.BEGINNER,
    estimated_reading_time: 8,
    view_count: 89,
    like_count: 12,
    created_at: '2024-01-08T11:00:00Z',
    updated_at: '2024-01-08T11:00:00Z',
    published_at: '2024-01-08T11:00:00Z'
  },
  {
    id: '3',
    title: 'Financer son projet ESS : Guide des dispositifs en Guadeloupe',
    description: 'Panorama complet des financements disponibles pour les projets ESS en Guadeloupe : subventions, prêts, fonds d\'investissement.',
    content: `# Financements ESS en Guadeloupe

## Financements Publics

### Région Guadeloupe
- **ARICE** : Aide Régionale à l'Initiative et à la Création d'Entreprise
- **ARDDA** : Aide Régionale au Développement Durable et à l'Aménagement
- **Fonds ESS** : Financement spécifique économie sociale

### Europe
- **FEDER** : Fonds Européen de Développement Régional
- **FSE+** : Fonds Social Européen Plus
- **LEADER** : Développement rural

## Financements Privés

### Banques Solidaires
- **Crédit Coopératif**
- **NEF** (Nouvelle Économie Fraternelle)
- **Caisse d'Épargne** (produits ESS)

### Fonds d'Investissement
- **SIFA** (Société d'Investissement France Active)
- **Fonds de cohésion sociale**

## Financement Participatif

### Plateformes
- **Ulule** : Projets créatifs et sociaux
- **KissKissBankBank** : Innovation sociale
- **HelloAsso** : Associations

## Micro-crédit

### Organismes
- **ADIE** : Micro-crédit professionnel
- **Initiative Guadeloupe**
- **PFIL** : Plateforme d'Initiative Locale`,
    type: ResourceType.GUIDE,
    category: 'FINANCING',
    tags: ['financement', 'subventions', 'ARICE', 'FEDER', 'micro-crédit'],
    author_id: 'coord-1',
    author_name: 'Marie-France Charles',
    is_published: true,
    is_featured: true,
    difficulty_level: DifficultyLevel.INTERMEDIATE,
    estimated_reading_time: 12,
    view_count: 234,
    like_count: 45,
    created_at: '2024-01-05T10:00:00Z',
    updated_at: '2024-01-12T16:00:00Z',
    published_at: '2024-01-05T10:00:00Z'
  },
  {
    id: '4',
    title: 'Checklist : Lancer sa communication ESS',
    description: 'Liste de vérification complète pour mettre en place une stratégie de communication efficace pour votre projet ESS.',
    content: `# Checklist Communication ESS

## Avant de commencer

- [ ] Définir ses objectifs de communication
- [ ] Identifier ses publics cibles
- [ ] Analyser la concurrence
- [ ] Définir son budget communication

## Identité visuelle

- [ ] Créer un logo professionnel
- [ ] Définir sa charte graphique
- [ ] Choisir ses couleurs et typographies
- [ ] Créer des templates de documents

## Supports de communication

- [ ] Site web ou page Facebook
- [ ] Plaquette de présentation
- [ ] Cartes de visite
- [ ] Flyers et affiches

## Communication digitale

- [ ] Créer ses comptes réseaux sociaux
- [ ] Planifier ses publications
- [ ] Créer du contenu régulier
- [ ] Interagir avec sa communauté

## Relations presse

- [ ] Identifier les médias locaux
- [ ] Rédiger un communiqué de presse
- [ ] Créer un dossier de presse
- [ ] Organiser un événement de lancement

## Mesure des résultats

- [ ] Définir ses indicateurs (KPIs)
- [ ] Utiliser Google Analytics
- [ ] Suivre l'engagement sur les réseaux
- [ ] Mesurer le retour sur investissement`,
    type: ResourceType.CHECKLIST,
    category: 'MARKETING',
    tags: ['communication', 'marketing', 'réseaux sociaux', 'identité visuelle'],
    author_id: 'coord-1',
    author_name: 'Sophie Bernard',
    is_published: true,
    is_featured: false,
    difficulty_level: DifficultyLevel.BEGINNER,
    estimated_reading_time: 5,
    view_count: 67,
    like_count: 8,
    created_at: '2024-01-12T14:00:00Z',
    updated_at: '2024-01-12T14:00:00Z',
    published_at: '2024-01-12T14:00:00Z'
  },
  {
    id: '5',
    title: 'Outils numériques gratuits pour l\'ESS',
    description: 'Sélection d\'outils numériques gratuits et utiles pour gérer et développer votre projet ESS.',
    content: `# Outils Numériques Gratuits ESS

## Gestion de projet

### **Trello**
- Gestion de tâches en mode Kanban
- Collaboration en équipe
- Templates pour projets ESS

### **Asana**
- Planification de projets
- Suivi des échéances
- Rapports d'avancement

## Communication

### **Canva**
- Création graphique simplifiée
- Templates ESS disponibles
- Exports haute qualité

### **Mailchimp**
- Newsletter et emailing
- Segmentation des contacts
- Statistiques détaillées

## Comptabilité

### **Wave**
- Comptabilité gratuite
- Facturation
- Suivi des dépenses

### **Dolibarr**
- ERP/CRM open source
- Gestion complète
- Communauté active

## Réseaux sociaux

### **Buffer**
- Programmation de posts
- Analyse des performances
- Gestion multi-comptes

### **Hootsuite**
- Tableau de bord unifié
- Veille concurrentielle
- Rapports automatiques

## Mesure d'impact

### **Google Analytics**
- Analyse audience web
- Suivi conversions
- Rapports personnalisés

### **SurveyMonkey**
- Enquêtes de satisfaction
- Mesure d'impact social
- Analyse des résultats`,
    type: ResourceType.TOOL,
    category: 'DIGITAL_TOOLS',
    tags: ['outils numériques', 'gratuit', 'gestion', 'communication', 'comptabilité'],
    author_id: 'coord-1',
    author_name: 'Pierre Dubois',
    is_published: true,
    is_featured: true,
    difficulty_level: DifficultyLevel.BEGINNER,
    estimated_reading_time: 10,
    view_count: 145,
    like_count: 28,
    created_at: '2024-01-07T16:00:00Z',
    updated_at: '2024-01-14T10:00:00Z',
    published_at: '2024-01-07T16:00:00Z'
  }
];

export const useResourceStore = create<ResourceState>((set, get) => ({
  resources: [],
  categories: MOCK_CATEGORIES,
  userResources: [],
  currentResource: null,
  visibilities: [],
  isLoading: false,
  error: null,

  fetchResources: async (filters?: ResourceFilters) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        // Use mock data
        let filteredResources = MOCK_RESOURCES;
        
        if (filters?.search) {
          const searchLower = filters.search.toLowerCase();
          filteredResources = filteredResources.filter(r => 
            r.title.toLowerCase().includes(searchLower) ||
            r.description.toLowerCase().includes(searchLower) ||
            r.tags.some(tag => tag.toLowerCase().includes(searchLower))
          );
        }
        
        if (filters?.category) {
          filteredResources = filteredResources.filter(r => r.category === filters.category);
        }
        
        if (filters?.type) {
          filteredResources = filteredResources.filter(r => r.type === filters.type);
        }
        
        if (filters?.difficulty) {
          filteredResources = filteredResources.filter(r => r.difficulty_level === filters.difficulty);
        }
        
        if (filters?.featured) {
          filteredResources = filteredResources.filter(r => r.is_featured);
        }
        
        set({ resources: filteredResources, isLoading: false });
        return;
      }

      let query = supabase
        .from('resources')
        .select(`
          *,
          author:users!resources_author_id_fkey(full_name)
        `)
        .eq('is_published', true)
        .order('created_at', { ascending: false });

      if (filters?.search) {
        query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }
      if (filters?.category) {
        query = query.eq('category', filters.category);
      }
      if (filters?.type) {
        query = query.eq('type', filters.type);
      }
      if (filters?.difficulty) {
        query = query.eq('difficulty_level', filters.difficulty);
      }
      if (filters?.featured) {
        query = query.eq('is_featured', true);
      }

      const { data, error } = await query;
      if (error) throw error;

      set({ resources: data || [], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchUserResources: async (userId?: string) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        // Mock: return resources based on user profile
        const userResources = MOCK_RESOURCES.slice(0, 3);
        set({ userResources, isLoading: false });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      const targetUserId = userId || user?.id;
      
      if (!targetUserId) {
        set({ userResources: [], isLoading: false });
        return;
      }

      // Get resources visible to this user
      const { data, error } = await supabase
        .from('resource_visibilities')
        .select(`
          resource:resources(
            *,
            author:users!resources_author_id_fkey(full_name)
          )
        `)
        .eq('user_id', targetUserId)
        .or('expires_at.is.null,expires_at.gt.now()');

      if (error) throw error;

      const userResources = data?.map(v => v.resource).filter(Boolean) || [];
      set({ userResources, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchResource: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        const resource = MOCK_RESOURCES.find(r => r.id === id);
        set({ currentResource: resource || null, isLoading: false });
        return;
      }

      const { data, error } = await supabase
        .from('resources')
        .select(`
          *,
          author:users!resources_author_id_fkey(full_name)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        set({ currentResource: null, error: 'Resource not found', isLoading: false });
        return;
      }
      set({ currentResource: data, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchCategories: async () => {
    try {
      const { data, error } = await supabase
        .from('resource_categories')
        .select('*')
        .eq('is_active', true)
        .order('order_index');

      if (error) {
        console.error('Error fetching categories:', error);
        // Fallback aux catégories par défaut si la table n'existe pas encore
        set({ categories: MOCK_CATEGORIES });
        return;
      }
      
      set({ categories: data && data.length > 0 ? data : MOCK_CATEGORIES });
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      set({ categories: MOCK_CATEGORIES });
    }
  },

  createResource: async (resourceData: CreateResourceForm) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré');
    }

    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('resources')
        .insert({
          ...resourceData,
          author_id: user.id,
          is_published: true,
          view_count: 0,
          like_count: 0
        })
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Failed to create resource');

      const { resources } = get();
      set({ resources: [data, ...resources], isLoading: false });
      return data;
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  updateResource: async (id: string, updates: Partial<Resource>) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré');
    }

    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('resources')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      const { resources, currentResource } = get();
      const updatedResources = resources.map(r => 
        r.id === id ? { ...r, ...updates } : r
      );
      
      set({
        resources: updatedResources,
        currentResource: currentResource?.id === id 
          ? { ...currentResource, ...updates } 
          : currentResource,
        isLoading: false
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  deleteResource: async (id: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré');
    }

    set({ isLoading: true, error: null });
    try {
      const { error } = await supabase
        .from('resources')
        .delete()
        .eq('id', id);

      if (error) throw error;

      const { resources } = get();
      set({ resources: resources.filter(r => r.id !== id), isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  assignResourceVisibility: async (assignment: VisibilityAssignment) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré');
    }

    set({ isLoading: true, error: null });
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const visibilityInserts = assignment.user_ids.map(userId => ({
        resource_id: assignment.resource_id,
        user_id: userId,
        granted_by: user.id,
        expires_at: assignment.expires_at,
        notes: assignment.notes
      }));

      const { error } = await supabase
        .from('resource_visibilities')
        .insert(visibilityInserts);

      if (error) throw error;

      // Refresh visibilities
      await get().fetchResourceVisibilities(assignment.resource_id);
      set({ isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  removeResourceVisibility: async (resourceId: string, userId: string) => {
    if (!isSupabaseConfigured()) {
      throw new Error('Supabase n\'est pas configuré');
    }

    try {
      const { error } = await supabase
        .from('resource_visibilities')
        .delete()
        .eq('resource_id', resourceId)
        .eq('user_id', userId);

      if (error) throw error;

      // Update local state
      const { visibilities } = get();
      set({ 
        visibilities: visibilities.filter(v => 
          !(v.resource_id === resourceId && v.user_id === userId)
        )
      });
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  fetchResourceVisibilities: async (resourceId: string) => {
    try {
      if (!isSupabaseConfigured()) {
        set({ visibilities: [] });
        return;
      }

      const { data, error } = await supabase
        .from('resource_visibilities')
        .select(`
          *,
          user:users!resource_visibilities_user_id_fkey(full_name, email),
          granter:users!resource_visibilities_granted_by_fkey(full_name)
        `)
        .eq('resource_id', resourceId);

      if (error) throw error;
      set({ visibilities: data || [] });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  recordInteraction: async (resourceId: string, type: string) => {
    try {
      if (!isSupabaseConfigured()) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase
        .from('resource_interactions')
        .insert({
          resource_id: resourceId,
          user_id: user.id,
          type
        });

      // Update view count if it's a view interaction
      if (type === 'VIEW') {
        const { resources, currentResource } = get();
        const updatedResources = resources.map(r => 
          r.id === resourceId ? { ...r, view_count: r.view_count + 1 } : r
        );
        
        set({
          resources: updatedResources,
          currentResource: currentResource?.id === resourceId 
            ? { ...currentResource, view_count: currentResource.view_count + 1 }
            : currentResource
        });
      }
    } catch (error) {
      console.error('Error recording interaction:', error);
    }
  },

  toggleLike: async (resourceId: string) => {
    try {
      if (!isSupabaseConfigured()) return;

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check if already liked
      const { data: existing } = await supabase
        .from('resource_interactions')
        .select('id')
        .eq('resource_id', resourceId)
        .eq('user_id', user.id)
        .eq('type', 'LIKE')
        .maybeSingle();

      if (existing) {
        // Unlike
        await supabase
          .from('resource_interactions')
          .delete()
          .eq('id', existing.id);
      } else {
        // Like
        await supabase
          .from('resource_interactions')
          .insert({
            resource_id: resourceId,
            user_id: user.id,
            type: 'LIKE'
          });
      }

      // Update like count
      const { resources, currentResource } = get();
      const increment = existing ? -1 : 1;
      const updatedResources = resources.map(r => 
        r.id === resourceId ? { ...r, like_count: r.like_count + increment } : r
      );
      
      set({
        resources: updatedResources,
        currentResource: currentResource?.id === resourceId 
          ? { ...currentResource, like_count: currentResource.like_count + increment }
          : currentResource
      });
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  },

  clearError: () => set({ error: null })
}));